<div class="discussion-detail tanuh-themed-page">
  <app-header></app-header>

  <div class="discussion-detail--content">
    <!-- Header Section -->
    <app-asset-header 
      [config]="headerConfig"
      variant="tanuh"
      [showActions]="true"
      [showLikeButton]="false"
      [showPinButton]="true"
      [showVoteButtons]="true"
      [isBookmarked]="discussion?.isBookmarked || false"
      [isPinned]="discussion?.isPinned || false"
      [userVote]="userVote"
      [voteCount]="voteCount"
      [isLoggedIn]="isLoggedIn"
      (bookmarkClick)="toggleBookmark()"
      (pinClick)="togglePin()"
      (upvoteClick)="toggleThumbsUp()">
    </app-asset-header>

    <!-- Login modal when user clicks Bookmark/Pin but is not logged in -->
    <app-login-modal
      *ngIf="showLoginModal"
      [visible]="showLoginModal"
      (visibleChange)="showLoginModal = $event"
    ></app-login-modal>

    <!-- Main Content -->
    <div class="discussion-detail--main">
      <div class="discussion-detail--container">
        <!-- Discussion Sidebar -->
        <aside class="discussion-detail--sidebar">
          <app-discussion-sidebar [activeTab]="activeTab"></app-discussion-sidebar>
        </aside>

        <!-- Loading State -->
        <div class="discussion-detail--loading" *ngIf="isLoading && !discussion">
          <div class="discussion-detail--loading-spinner">
            <div class="spinner"></div>
            <p>Loading discussion...</p>
          </div>
        </div>

        <!-- Error State -->
        <div class="discussion-detail--error" *ngIf="!isLoading && errorMessage && !discussion">
          <div class="discussion-detail--error-content">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" stroke-width="2"/>
              <line x1="12" y1="8" x2="12" y2="12" stroke="#ef4444" stroke-width="2" stroke-linecap="round"/>
              <circle cx="12" cy="16" r="1" fill="#ef4444"/>
            </svg>
            <h3>{{ errorMessage }}</h3>
            <button (click)="loadDiscussion(discussionId!)" class="discussion-detail--retry-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Retry
            </button>
          </div>
        </div>

        <!-- Combined Content and Details Section -->
        <div class="discussion-detail--combined-section" *ngIf="!isLoading && discussion">
          <!-- Admin review comment (owner only) -->
          <div
            class="discussion-detail--admin-warning"
            *ngIf="isDiscussionOwner && adminComment"
            role="status"
          >
            <div class="discussion-detail--admin-warning-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="discussion-detail--admin-warning-body">
              <strong class="discussion-detail--admin-warning-title">Platform Admin Comment</strong>
              <p class="discussion-detail--admin-warning-text">{{ adminComment }}</p>
            </div>
            <button
              type="button"
              class="discussion-detail--admin-warning-edit-btn"
              (click)="navigateToEdit()"
              title="Edit discussion"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Edit Discussion</span>
            </button>
          </div>
          <div class="discussion-detail--content-row">
            <!-- Main Content Section -->
            <main class="discussion-detail--main-content">
              <article class="discussion-detail--content-section">
                <header class="discussion-detail--content-header">
                  <div class="discussion-detail--author-section">
                    <div class="discussion-detail--author-avatar">
                      <span class="discussion-detail--author-avatar-text">{{ getAuthorInitials() }}</span>
                    </div>
                    <div class="discussion-detail--author-info">
                      <div class="discussion-detail--author-header">
                        <h3 class="discussion-detail--author-name">{{ discussion.author }}</h3>
                      </div>
                      <div class="discussion-detail--status-row" *ngIf="discussion.status">
                        <span class="discussion-detail--status-badge" 
                              [class.status-approved]="discussion.status === 'APPROVED'"
                              [class.status-pending]="discussion.status === 'PENDING'"
                              [class.status-rejected]="discussion.status === 'REJECTED'">
                          {{ getStatusLabel(discussion.status) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </header>
                <div class="discussion-detail--content-body">{{ getPlainTextContent() }}</div>
              </article>
            </main>

            <!-- Right sidebar: Discussion Details + Tags -->
            <aside class="discussion-detail--meta-sidebar">
              <div class="discussion-detail--details-section">
                <div class="discussion-detail--details-header">
                  <h3 class="discussion-detail--details-title">Discussion Details</h3>
                </div>
                <div class="discussion-detail--details-list">
                  <div class="discussion-detail--detail-item" *ngIf="discussion?.status">
                    <div class="discussion-detail--detail-content">
                      <div class="discussion-detail--detail-label">Status:</div>
                      <div class="discussion-detail--detail-value">
                        <span class="discussion-detail--status-badge-pill" [class.status-approved]="discussion?.status === 'APPROVED'" [class.status-pending]="discussion?.status === 'PENDING'" [class.status-rejected]="discussion?.status === 'REJECTED'">
                          {{ getStatusLabel(discussion?.status) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="discussion-detail--detail-item discussion-detail--detail-item-subcategory" *ngIf="discussion?.category">
                    <div class="discussion-detail--detail-content">
                      <div class="discussion-detail--detail-label">{{ getSubCategoryLabel() }}:</div>
                      <div class="discussion-detail--detail-value discussion-detail--detail-value-with-link">
                        <span class="discussion-detail--subcategory-value" [title]="getSubCategoryDisplayText()">{{ getSubCategoryDisplayText() }}</span>
                        <button *ngIf="discussion?.subCategoryId" class="discussion-detail--detail-link-btn" type="button" (click)="navigateToAsset()" title="View asset details">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <polyline points="15 3 21 3 21 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="discussion-detail--detail-item">
                    <div class="discussion-detail--detail-content">
                      <div class="discussion-detail--detail-label">Attachment:</div>
                      <div class="discussion-detail--detail-value discussion-detail--detail-value-with-link">
                        <a *ngIf="getFirstAttachmentUrl()" [href]="getFirstAttachmentUrl()" target="_blank" rel="noopener noreferrer" [attr.download]="getFirstAttachmentName() || null" class="discussion-detail--detail-link">{{ getFirstAttachmentName() || 'Download' }}</a>
                        <button *ngIf="!getFirstAttachmentUrl() && getFirstAttachment()" type="button" class="discussion-detail--detail-attachment-download-btn" (click)="downloadAttachment(getFirstAttachment()!)" [disabled]="isDownloadingAttachment">
                          {{ isDownloadingAttachment ? 'Downloading...' : getFirstAttachmentName() }}
                        </button>
                        <span *ngIf="!getFirstAttachment() && !getFirstAttachmentName()">—</span>
                      </div>
                    </div>
                  </div>
                  <div class="discussion-detail--detail-item" *ngIf="discussion?.created_at">
                    <div class="discussion-detail--detail-content">
                      <div class="discussion-detail--detail-label">Created At:</div>
                      <div class="discussion-detail--detail-value">{{ formatDetailDate(discussion?.created_at) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tags section -->
              <div class="discussion-detail--tags-section" *ngIf="discussion?.tags?.length">
                <div class="discussion-detail--tags-header">
                  <h3 class="discussion-detail--tags-title">Tags</h3>
                </div>
                <div class="discussion-detail--tags-list">
                  <span
                    *ngFor="let tag of discussion.tags"
                    class="discussion-detail--tag-pill"
                  >{{ tag }}</span>
                </div>
              </div>
            </aside>
          </div>

          <!-- Comments Section (inside combined-section so it sits right below content) -->
          <div class="discussion-detail--comments-section" *ngIf="discussion">
            <h3 class="discussion-detail--comments-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Comments ({{ topLevelCommentCountExcludingRejected }})
            </h3>

            <!-- Comments List -->
            <div class="discussion-detail--comments-list">
              <div class="discussion-detail--comment-item" *ngFor="let comment of comments" [class.pending]="isPending(comment)" [class.rejected]="isRejected(comment)">
                <div class="discussion-detail--comment-header">
                  <div class="discussion-detail--comment-author-avatar">
                    <span>{{ comment.author.charAt(0) }}</span>
                  </div>
                  <div class="discussion-detail--comment-main">
                    <div class="discussion-detail--comment-author-info">
                      <div class="discussion-detail--comment-author-row">
                        <span class="discussion-detail--comment-author-name">{{ comment.author }}</span>
                        <span class="discussion-detail--comment-timestamp">{{ comment.timestamp }}</span>
                        <span *ngIf="isRejected(comment)" class="discussion-detail--comment-rejected-badge">Rejected</span>
                      </div>
                    </div>

                    <div class="discussion-detail--comment-content" [innerHTML]="comment.content" [class.pending-content]="isPending(comment)" [class.rejected-content]="isRejected(comment)">
                    </div>
                    
                    <!-- Pending Review message (no badge) -->
                    <div *ngIf="isPending(comment)" class="discussion-detail--comment-pending-message">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span>Your comment is pending admin approval and will be visible once approved.</span>
                    </div>
                    
                    <!-- Comment Actions (Hidden for pending comments) -->
                    <div class="discussion-detail--comment-actions" *ngIf="isApproved(comment)">
                      <!-- Reply Button (First) -->
                      <button 
                        class="discussion-detail--comment-action-btn discussion-detail--comment-reply-btn"
                        (click)="openReplyEditor(comment.id)"
                        [title]="'Reply' + (getReplyCount(comment) > 0 ? ' (' + getReplyCount(comment) + ')' : '')"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"/>
                        </svg>
                        <span class="discussion-detail--comment-action-count">{{ getReplyCount(comment) }}</span>
                      </button>

                      <!-- Upvote Button (Second) -->
                      <button 
                        class="discussion-detail--comment-action-btn discussion-detail--comment-upvote-btn"
                        [class.active]="comment.userUpvoted"
                        (click)="toggleCommentUpvote(comment)"
                        title="Upvote"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M7 13l5-5 5 5M7 6l5-5 5 5"/>
                        </svg>
                        <span class="discussion-detail--comment-action-count">{{ comment.upvotes ?? 0 }}</span>
                      </button>

                      <!-- Reaction Button (Third) -->
                      <div class="discussion-detail--comment-reaction-wrapper">
                        <button 
                          class="discussion-detail--comment-action-btn discussion-detail--comment-reaction-btn"
                          (click)="toggleReactionPicker(comment.id, $event)"
                          title="Add Reaction"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                            <line x1="9" y1="9" x2="9.01" y2="9"/>
                            <line x1="15" y1="9" x2="15.01" y2="9"/>
                          </svg>
                        </button>
                        
                        <!-- Reaction Picker -->
                        <div class="discussion-detail--reaction-picker" *ngIf="showReactionPicker[comment.id]">
                          <button
                            *ngFor="let reaction of availableReactions"
                            class="discussion-detail--reaction-option"
                            [class.active]="hasReaction(comment, reaction)"
                            [class.disabled]="!canAddReaction(comment, reaction)"
                            [disabled]="!canAddReaction(comment, reaction)"
                            (click)="toggleReaction(comment, reaction); toggleReactionPicker(comment.id, $event)"
                            [title]="canAddReaction(comment, reaction) ? reaction : 'You can add only one reaction per comment'"
                          >
                            {{ reaction }}
                            <span *ngIf="getReactionCount(comment, reaction) > 0" class="discussion-detail--reaction-count">{{ getReactionCount(comment, reaction) }}</span>
                          </button>
                        </div>

                        <!-- Display Active Reactions (all from API, so selected emoji like 🔥 shows) -->
                        <div class="discussion-detail--comment-reactions-display" *ngIf="hasReactions(comment)">
                          <ng-container *ngFor="let reaction of getReactionKeys(comment)">
                            <span
                              class="discussion-detail--reaction-badge"
                              [class.active]="hasReaction(comment, reaction)"
                              (click)="toggleReaction(comment, reaction)"
                              [title]="reaction + ' (' + getReactionCount(comment, reaction) + ')'"
                            >
                              {{ reaction }}
                              <span class="discussion-detail--reaction-badge-count">{{ getReactionCount(comment, reaction) }}</span>
                            </span>
                          </ng-container>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Replies toggle and list -->
                <div class="discussion-detail--comment-replies-wrap" *ngIf="comment.replies && comment.replies.length > 0">
                  <button
                    type="button"
                    class="discussion-detail--replies-toggle"
                    (click)="toggleReplies(comment.id)"
                    [attr.aria-expanded]="isRepliesExpanded(comment.id)"
                  >
                    <svg class="discussion-detail--replies-toggle-chevron" [class.expanded]="isRepliesExpanded(comment.id)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    <span *ngIf="isRepliesExpanded(comment.id)">Hide {{ getReplyCount(comment) }} repl{{ getReplyCount(comment) === 1 ? 'y' : 'ies' }}</span>
                    <span *ngIf="!isRepliesExpanded(comment.id)">View {{ getReplyCount(comment) }} repl{{ getReplyCount(comment) === 1 ? 'y' : 'ies' }}</span>
                  </button>
                  <div class="discussion-detail--comment-replies" *ngIf="isRepliesExpanded(comment.id)">
                  <div class="discussion-detail--comment-item discussion-detail--comment-item-reply" *ngFor="let reply of comment.replies" [class.pending]="isPending(reply)" [class.rejected]="isRejected(reply)">
                    <div class="discussion-detail--comment-header">
                      <div class="discussion-detail--comment-author-avatar">
                        <span>{{ reply.author.charAt(0) }}</span>
                      </div>
                      <div class="discussion-detail--comment-main">
                        <div class="discussion-detail--comment-author-info">
                          <div class="discussion-detail--comment-author-row">
                            <span class="discussion-detail--comment-author-name">{{ reply.author }}</span>
                            <span class="discussion-detail--comment-timestamp">{{ reply.timestamp }}</span>
                            <span *ngIf="isRejected(reply)" class="discussion-detail--comment-rejected-badge">Rejected</span>
                          </div>
                        </div>
                        <div class="discussion-detail--comment-content" [innerHTML]="reply.content" [class.pending-content]="isPending(reply)" [class.rejected-content]="isRejected(reply)">
                        </div>
                        
                        <!-- Pending Review message (no badge) -->
                        <div *ngIf="isPending(reply)" class="discussion-detail--comment-pending-message">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                          <span>Your reply is pending admin approval and will be visible once approved.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
            </div>
            </div>

            <!-- Comment composer (below the thread) -->
            <div class="discussion-detail--comment-form">
              <div class="discussion-detail--comment-form-header">
                <div class="discussion-detail--comment-author-avatar">
                  <span>U</span>
                </div>
                <div class="discussion-detail--comment-form-content">
                  <div
                    class="discussion-detail--comment-editor-trigger"
                    (click)="openCommentEditor()"
                    [class.has-content]="hasContent(newComment)"
                  >
                    <div
                      class="discussion-detail--comment-editor-preview"
                      [innerHTML]="newComment || ''"
                    ></div>
                    <div
                      class="discussion-detail--comment-editor-placeholder"
                      *ngIf="!hasContent(newComment)"
                    >
                      Start a discussion... Share your thoughts, ask questions, or provide insights.
                    </div>
                    <div class="discussion-detail--comment-editor-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-8"></path>
                        <polyline points="18 2 22 6 18 10"></polyline>
                        <line x1="16" y1="6" x2="22" y2="6"></line>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <app-footer></app-footer>

  <!-- Comment Editor Modal -->
  <app-modal
    [(visible)]="showCommentEditorModal"
    [header]="replyingToCommentId ? 'Reply to Comment' : 'Add Comment'"
    [width]="'900px'"
    [showFooter]="true"
    [confirmLabel]="'Save'"
    [cancelLabel]="'Cancel'"
    [confirmButtonDisabled]="!hasCommentTextForSave()"
    (onConfirm)="saveComment()"
    (onCancel)="cancelComment()"
    (onClose)="onCommentEditorModalClose()"
  >
    <div class="discussion-detail--editor-modal">
      <p-editor
        [ngModel]="replyingToCommentId ? replyContent : newComment"
        (ngModelChange)="replyingToCommentId ? (replyContent = $event) : (newComment = $event)"
        [style]="{ height: '400px' }"
        [modules]="editorModules"
        [placeholder]="replyingToCommentId ? 'Enter your reply...' : 'Enter your comment...'"
        (onInit)="onEditorInit($event)"
      ></p-editor>
    </div>
  </app-modal>
</div>
